import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { CircularProgress } from '@opendoor/mosaic';

import AddressHeader from '../components/WorkOrderProgress/AddressHeader';
import Notes from '../components/WorkOrderProgress/Notes';
import WorkOrderNavigationBar from '../components/WorkOrderProgress/WorkOrderNavigationBar';
import { useGetWorkOrder, useGetWorkOrderMessageThread } from '../graphql/queries/homework';
import { isOlderThan48Hours } from '../utils/date';
import NotFoundErrorPage from './NotFoundErrorPage';

const WorkOrderProgress = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const [threadWorkOrderId, setThreadWorkOrderId] = React.useState<string | null>(null);
  const {
    data: workOrder,
    loading: workOrderLoading,
    error: workOrderError,
  } = useGetWorkOrder({ variables: { id: workOrderId ?? '' } });

  // using state and useEffect to handle the case where the workOrderId is not available immediately
  // from the previous getWorkOrder query
  useEffect(() => {
    if (workOrder?.id) {
      setThreadWorkOrderId(workOrder.id);
    }
  }, [workOrder?.id]);

  const {
    data: threads,
    loading: messageThreadLoading,
    error: messageThreadError,
  } = useGetWorkOrderMessageThread({
    variables: { workOrderId: threadWorkOrderId ?? '' },
    skip: !threadWorkOrderId,
  });

  const TPMessageThread = threads?.messageThread?.notes?.[0]?.createdAt ?? null;
  const textboxPlaceholderText = 'Add any comment here';

  if (workOrderError || messageThreadError) {
    return <NotFoundErrorPage />;
  }

  if (workOrderLoading || messageThreadLoading) {
    return <CircularProgress className="mh-auto" size={72} thickness={5} />;
  }

  return (
    <div>
      <AddressHeader address={workOrder?.address ?? null} />
      <WorkOrderNavigationBar workOrderId={workOrderId ?? ''} />
      <div style={styles.notesWrapper as React.CSSProperties}>
        <Notes
          title="Notes with Opendoor only"
          placeHolderText={textboxPlaceholderText}
          workOrderId={workOrder?.id ?? ''}
          isOnsite={false}
          needsUpdate={isOlderThan48Hours(TPMessageThread)}
        />
        <Notes
          title="Notes with On-Site Worker only"
          placeHolderText={textboxPlaceholderText}
          workOrderId={workOrder?.id ?? ''}
          isOnsite={true}
          needsUpdate={false} // OnSiteWorker Message Thread does not have `no updates since 48 hrs` tag.
        />
      </div>
    </div>
  );
};

const styles = {
  notesWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    background: '#f0f0f0',
  },
};

export default WorkOrderProgress;
